import { useCallback, useEffect, useMemo, useState } from "react";
import { useDrop, DropTargetMonitor, useDragLayer, DragLayerMonitor } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import {
  addTemporaryPairing,
  removeCrewTemporaryPairing,
  updatePairingEditor,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { IPosition, IFullPairingPosition, IPairing } from "@vmosolutions/oc-interfaces";
import { CrewListForm } from "./Forms/CrewPositionsForm/CrewListForm";
// Styles
import { Box, Theme } from "@mui/material";
import { openSnackbar } from "src/redux/actions/App.Actions";
import { ICrewDraggable } from "../PairingEditorWindow/CrewMember";
import * as _ from "lodash";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface ICrewPositionDropTargetProps {
  id: string;
  windowId: string;
  path: string[];
  initPairing: IPairing;
}

export const CrewPositionDropTarget = (props: ICrewPositionDropTargetProps): JSX.Element => {
  const { initPairing } = props;
  const dispatch = useDispatch();
  const storedPairing = useSelector((state: IRootState) => state.crewSchedule.pairingEditors[props.id]);
  const positions = useMemo(() => storedPairing?.positions ?? [], [storedPairing]);
  const [prevPositions, setPrevPostions] = useState<IFullPairingPosition[]>(positions);
  // Monitor item being dragged, return as valid if type matches accepted.
  const { isValidDraggable } = useDragLayer((monitor: DragLayerMonitor<ICrewDraggable>) => {
    const draggedItem = monitor.getItem();
    return {
      isValidDraggable:
        monitor.isDragging() && (draggedItem?.type === "CREW_ASSIGN" || draggedItem?.type === "CREW_REASSIGN"),
    };
  });

  const formIsDirty = useMemo(
    () => _.isEqual(storedPairing?.positions, initPairing?.positions),
    [storedPairing?.positions, initPairing?.positions]
  );

  const updateTempParingsByPostions = useCallback(
    (positions: IFullPairingPosition[], prevPositions: IFullPairingPosition[]) => {
      if (formIsDirty) {
        const originalCrew = prevPositions.map((crew: IFullPairingPosition) => crew?.crew?._id);
        const currentCrew = positions.map((crew: IFullPairingPosition) => crew?.crew?._id);
        //find the difference...
        const addedCrew = _.difference(currentCrew, originalCrew);
        const removedCrew = _.difference(originalCrew, currentCrew);
        if (removedCrew.length > 0) {
          //remove crew from pairing
          removedCrew.forEach((each: string) => {
            if (each) dispatch(removeCrewTemporaryPairing(storedPairing?._id, each));
          });
        }
        if (addedCrew.length > 0) {
          //add crew from pairing
          addedCrew.forEach((each: string) => {
            if (each) {
              dispatch(
                addTemporaryPairing({
                  start: storedPairing?.approxStart,
                  end: storedPairing?.approxEnd,
                  pairingId: storedPairing?._id,
                  crewId: each,
                })
              );
            }
          });
        }
        setPrevPostions(positions);
      }
    },
    [dispatch, formIsDirty, storedPairing]
  );

  useEffect(() => {
    updateTempParingsByPostions(positions, prevPositions);
    setPrevPostions(positions);
  }, [formIsDirty, positions, prevPositions, updateTempParingsByPostions]);

  const [{ isOver }, drop] = useDrop({
    accept: ["CREW_ASSIGN"],
    drop(i: any, monitor: DropTargetMonitor) {
      const item: ICrewDraggable = i;
      let updateField = false;

      // Create deep clone to stop mutation of initialized value as it won't detect deep changes.
      const newPositions = _.cloneDeep(positions);

      const isAssigned = positions.findIndex((pairingPosition: IFullPairingPosition) => {
        return pairingPosition.crew?._id === item.crewMember._id;
      });

      const crewPositions = item?.crewMember?.crewInfo?.positions ?? [];

      // Add crew member if there's an available position and he isn't already assigned.
      if (isAssigned === -1) {
        for (let i = 0; i < positions.length; i++) {
          // If there's an available crew slow add crew if endorsed and break out of loop.
          if (!positions[i].crew) {
            const isEndorsed = crewPositions.find(
              (crewPosition: IPosition) => crewPosition._id === positions[i].position._id
            );
            if (isEndorsed) {
              newPositions[i].crew = item.crewMember;
              updateField = true;
              break;
            }
          }
        }

        // If user wasn't assigned to an available slot, create a new one for them and assign them.
        if (!updateField) {
          //console.log("new position created for crew member.");
          const newCrewPos: IFullPairingPosition = {
            crew: item.crewMember,
            position: item.crewMember?.crewInfo?.positions[0],
          };
          newPositions.push(newCrewPos);
          updateField = true;
        }
      } else {
        // CREW ALREADY ASSIGNED, warn user.
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: 3000,
            variant: "info",
            message: "Crew member already assigned a position on this pairing.",
          })
        );
      }

      if (updateField) {
        dispatch(updatePairingEditor(props.id, { positions: newPositions }));
      }
    },

    canDrop(i: any, monitor: DropTargetMonitor) {
      const item: ICrewDraggable = i;
      return item?.crewMember?._id && item.crewMember.crewInfo && item.crewMember?.crewInfo?.positions?.length > 0;
    },

    collect(monitor: DropTargetMonitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
  });

  const DropZoneStyles = (theme: Theme): any => ({
    event_dropzone: {
      display: "flex",
      cursor: "pointer",
      minHeight: "100px",
      justifyContent: "center",
      alignItems: "flex-start",
      flexDirection: "column",
      flexGrow: 1,
      backgroundColor: theme.palette.background.paper,
      borderRadius: "3px",
      padding: "4px",
      "& .title": { fontSize: 18, fontWeight: "bold" },
      "& .body": {
        display: "flex",
        border: `2x solid ${theme.custom.forms.inputBorder}`,
        minHeight: "40px",
        alignItems: "center",
        color: theme.custom.forms.focusedColor,
        fontSize: 12,
        fontWeight: 300,
        textAlign: "center",
        padding: "4px 8px",
        opacity: 0.5,
        userSelect: "none",
      },
      "&.disabled": {
        opacity: 0.5,
        userSelect: "none",
        pointerEvents: "none",
      },
      "&.reject": {
        backgroundcolor: theme.custom.forms.errorColor,
      },
      "&.active": {
        backgroundcolor: theme.custom.forms.focusedColor,
      },
      "&.dirty": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
      "&.over": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
    },
  });

  return (
    <Box
      className={`${isValidDraggable && "active"} ${isOver && "over"}`}
      ref={drop}
      sx={(theme) => DropZoneStyles(theme).event_dropzone}
    >
      <div className="body">
        To add crew to this pairing, drag/drop them into available slots from the crew schedule.
      </div>
      <div style={{ display: "flex", width: "100%" }}>
        <CrewListForm {...props} />
      </div>
    </Box>
  );
};
